import type React from "react";
import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useMatch } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetSmartCommentReplies } from "api/hooks/useGetSmartCommentReplies";
import { useProfileContext } from "contexts/ProfileContext";
import { sortActivities } from "routes/patients/utils/activityHelpers";
import type { PreviewActivity } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { ActivityPreviewModal } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { Hidden } from "shared/atoms/Hidden";
import { ProtocolContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";

import { CalendarCard } from "../CalendarCard";
import { ProtocolActivityGroup } from "../ProtocolActivityGroup";
import type { ProtocolCard } from "../ProtocolTimeline/types";
import { isExercise, isLesson } from "../ProtocolTimeline/utils";

import { DayListWrapper, DayTab, DayTabs } from "./styles";

interface Day {
  date: string;
  activities: ProtocolCard[];
}

interface CalendarProps {
  days: Day[];
  patient_id: number;
  onSelectChange: (day: number) => void;
}

export const Calendar: React.FC<CalendarProps> = ({ days, patient_id, onSelectChange }) => {
  const { t } = useTranslation();
  const { format, isToday, parseISO } = useLocalizedDate();
  const { profile } = useProfileContext();
  const { selectedDayMobile, setSelectedDayMobile } = useContext(ProtocolContext);
  const [selected, setSelected] = useState(0);
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);
  const match = useMatch("*");

  invariant(profile);

  const { data: smartReplies } = useGetSmartCommentReplies({
    therapistId: profile?.id,
    patientId: patient_id,
  });

  const getSmartReply = (user_activity_id: number | null): string | null | undefined => {
    if (user_activity_id && smartReplies?.comments && smartReplies.comments.length > 0) {
      return smartReplies.comments.find(reply => reply.user_activity_id === user_activity_id)?.body;
    }
    return null;
  };

  const getSmartReplyId = (user_activity_id: number | null): number | null | undefined => {
    if (user_activity_id && smartReplies?.comments && smartReplies.comments.length > 0) {
      return smartReplies.comments.find(reply => reply.user_activity_id === user_activity_id)?.id;
    }
    return null;
  };

  useEffect(() => {
    if (selectedDayMobile !== null) {
      setSelected(selectedDayMobile);
      onSelectChange(selectedDayMobile);
      setSelectedDayMobile(null);
    } else {
      const today = days.findIndex(day => isToday(parseISO(day.date)));
      if (today !== -1) {
        setSelected(today);
        onSelectChange(today);
      } else {
        setSelected(0);
        onSelectChange(0);
      }
    }
  }, [match?.pathname]);

  const handlePreview = (activity: ProtocolCard) => {
    if (isExercise(activity)) {
      setPreviewActivity({
        id: activity.activityID,
        part: activity.current_level,
        type: "exercise",
      });
    } else if (isLesson(activity)) {
      setPreviewActivity({
        id: activity.activityID,
        part: activity.part,
        type: "lesson",
      });
    }
  };

  return (
    <Container data-testid="calendar">
      {/* Desktop Week View - All days visible */}
      <Hidden type="belowLarge">
        <CalendarWrapper>
          <WeekGrid>
            {days.map(day => {
              const { activities } = day;

              return (
                <DayColumn key={day.date} $isCurrent={isToday(parseISO(day.date))}>
                  <DayHeader $isCurrent={isToday(parseISO(day.date))}>
                    <DayName>{format(parseISO(day.date), "ccc")}</DayName>
                    <DayDate>{format(parseISO(day.date), t("common.date.noYear"))}</DayDate>
                  </DayHeader>

                  <ActivitiesList>
                    {activities.length === 0 ? (
                      <EmptyState>{t("patients.protocol.no_activities")}</EmptyState>
                    ) : (
                      activities.sort(sortActivities).map(activity => {
                        return (
                          <CalendarCard
                            key={`${day.date}_${activity.activity_type_trimmed}_${activity.id}_${activity.user_activity_schedule_id}`}
                            activity={activity}
                            smartReply={getSmartReply(activity.user_activity_id)}
                            smartReplyId={getSmartReplyId(activity.user_activity_id)}
                            isPast
                            activity_type={activity.activity_type_trimmed}
                            completed={activity.completed_on}
                            max_level={"max_level" in activity ? activity.max_level : undefined}
                            current_level={"current_level" in activity ? activity.current_level : undefined}
                            onPreview={
                              ["exercise", "lesson"].includes(activity.activity_type_trimmed)
                                ? () => handlePreview(activity)
                                : undefined
                            }
                          />
                        );
                      })
                    )}
                  </ActivitiesList>
                </DayColumn>
              );
            })}
          </WeekGrid>
        </CalendarWrapper>
      </Hidden>

      {/* Mobile Day Selector + Single Day View */}
      <Hidden type="aboveLarge">
        <DayListWrapper>
          <DayTabs>
            {days.map((day, index) => (
              <DayTab
                key={day.date}
                $current={isToday(parseISO(day.date))}
                $selected={index === selected}
                onClick={() => {
                  setSelected(days.indexOf(day));
                  onSelectChange(days.indexOf(day) + 1);
                }}
              >
                {`${format(parseISO(day.date), "ccc")} `}
                <Hidden type="belowTablet">{format(parseISO(day.date), t("common.date.noYear"))}</Hidden>
              </DayTab>
            ))}
          </DayTabs>
        </DayListWrapper>

        <MobileContent>
          {(() => {
            const selectedDay = days[selected];
            if (!selectedDay) return null;

            const { activities } = selectedDay;

            return activities.length === 0 ? (
              <EmptyState>{t("patients.protocol.no_activities")}</EmptyState>
            ) : (
              <ProtocolActivityGroup activities={activities} isOverdue={false} />
            );
          })()}
        </MobileContent>
      </Hidden>

      {previewActivity && (
        <ActivityPreviewModal
          activity={previewActivity}
          onClose={() => {
            setPreviewActivity(null);
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 100%;
`;

const CalendarWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 24px 30px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  box-sizing: border-box;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
    margin: 0 20px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 320px);
  gap: 1px;
  background-color: #e8ecf1;
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  overflow: hidden;
  width: fit-content;
  min-width: min-content;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin: 0 auto;
`;

const DayColumn = styled.div<{ $isCurrent: boolean }>`
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  width: 100%;

  ${({ $isCurrent }) =>
    $isCurrent &&
    `
    background-color: #f8fafc;
  `}
`;

const DayHeader = styled.div<{ $isCurrent: boolean }>`
  padding: 16px 12px;
  border-bottom: 2px solid ${({ $isCurrent }) => ($isCurrent ? "#0066cc" : "#e8ecf1")};
  background-color: ${({ $isCurrent }) => ($isCurrent ? "#e3f2fd" : "#fafbfc")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DayName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #5c6f82;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayDate = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1a2332;
`;

const ActivitiesList = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  padding: 40px 12px;
  text-align: center;
  font-size: 14px;
  color: #8391a1;
`;

const MobileContent = styled.div`
  padding: 20px;
`;
